/* eslint-disable react/prop-types, no-unused-vars */

import { Button, Form, Input, Select, Space } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';

const operationOptions = [
    { label: 'Sum', value: 'sum' },
    { label: 'Count rows', value: 'count' },
    { label: 'Minimum', value: 'min' },
    { label: 'Maximum', value: 'max' },
    { label: 'First by sort', value: 'first' },
    { label: 'Last by sort', value: 'last' },
];

const comparisonOptions = [
    { label: 'Number', value: 'number' },
    { label: 'Date / time', value: 'date' },
    { label: 'Text', value: 'string' },
];

const sortDirectionOptions = [
    { label: 'Ascending', value: 'asc' },
    { label: 'Descending', value: 'desc' },
];

const columnOptions = (columns) => columns.map(c => ({ label: c, value: c }));

const PivotAggregateForm = ({ config, columns, onChange }) => {
    const initialValues = {
        groupBy: [],
        aggregations: [],
        ...config,
    };

    return (
        <Form
            layout="vertical"
            initialValues={initialValues}
            onValuesChange={(_, values) => onChange(values)}
        >
            <Form.Item
                label="Group By"
                name="groupBy"
                rules={[{ required: true, type: 'array', min: 1, message: 'Select at least one grouping column' }]}
            >
                <Select
                    mode="multiple"
                    placeholder="Select grouping column"
                    options={columnOptions(columns)}
                    data-testid="pipeline-pivot-group-by"
                />
            </Form.Item>

            <Form.List
                name="aggregations"
                rules={[
                    {
                        validator: async (_, aggregations) => {
                            if (!aggregations || aggregations.length < 1) {
                                return Promise.reject(new Error('Add at least one aggregation'));
                            }
                            return Promise.resolve();
                        },
                    },
                ]}
            >
                {(fields, { add, remove }, { errors }) => (
                    <div className="flex flex-col gap-4">
                        {fields.map(({ key, name, ...restField }) => (
                            <div key={key} className="rounded border border-gray-200 p-3">
                                <div className="mb-2 flex items-center justify-between">
                                    <span className="text-sm font-semibold">Aggregation {name + 1}</span>
                                    <Button
                                        type="text"
                                        danger
                                        icon={<DeleteOutlined />}
                                        onClick={() => remove(name)}
                                        data-testid={`pipeline-pivot-remove-aggregation-${name}`}
                                    />
                                </div>

                                <Form.Item
                                    {...restField}
                                    label="Operation"
                                    name={[name, 'operation']}
                                    rules={[{ required: true, message: 'Select an operation' }]}
                                >
                                    <Select
                                        placeholder="Select operation"
                                        options={operationOptions}
                                        data-testid={`pipeline-pivot-operation-${name}`}
                                    />
                                </Form.Item>

                                <Form.Item noStyle shouldUpdate>
                                    {({ getFieldValue }) => {
                                        const operation = getFieldValue(['aggregations', name, 'operation']);
                                        const needsSource = operation && operation !== 'count';
                                        const needsComparison = operation === 'min' || operation === 'max';
                                        const needsSort = operation === 'first' || operation === 'last';

                                        return (
                                            <>
                                                {needsSource && (
                                                    <Form.Item
                                                        {...restField}
                                                        label="Source Column"
                                                        name={[name, 'sourceColumn']}
                                                        rules={[{ required: true, message: 'Select a source column' }]}
                                                    >
                                                        <Select
                                                            placeholder="Select source column"
                                                            options={columnOptions(columns)}
                                                            data-testid={`pipeline-pivot-source-column-${name}`}
                                                        />
                                                    </Form.Item>
                                                )}

                                                {needsComparison && (
                                                    <Form.Item
                                                        {...restField}
                                                        label="Comparison Type"
                                                        name={[name, 'comparisonType']}
                                                        rules={[{ required: true, message: 'Select how values should be compared' }]}
                                                    >
                                                        <Select
                                                            placeholder="Select comparison type"
                                                            options={comparisonOptions}
                                                            data-testid={`pipeline-pivot-comparison-type-${name}`}
                                                        />
                                                    </Form.Item>
                                                )}

                                                {needsSort && (
                                                    <Space direction="vertical" className="w-full" size={0}>
                                                        <Form.Item
                                                            {...restField}
                                                            label="Sort Column"
                                                            name={[name, 'sortColumn']}
                                                            rules={[{ required: true, message: 'Select a sort column' }]}
                                                        >
                                                            <Select
                                                                placeholder="Select sort column"
                                                                options={columnOptions(columns)}
                                                                data-testid={`pipeline-pivot-sort-column-${name}`}
                                                            />
                                                        </Form.Item>
                                                        <Form.Item
                                                            {...restField}
                                                            label="Sort Direction"
                                                            name={[name, 'sortDirection']}
                                                            rules={[{ required: true, message: 'Select a sort direction' }]}
                                                        >
                                                            <Select
                                                                placeholder="Select sort direction"
                                                                options={sortDirectionOptions}
                                                                data-testid={`pipeline-pivot-sort-direction-${name}`}
                                                            />
                                                        </Form.Item>
                                                        <Form.Item
                                                            {...restField}
                                                            label="Sort Comparison Type"
                                                            name={[name, 'sortComparisonType']}
                                                            rules={[{ required: true, message: 'Select how sort values should be compared' }]}
                                                        >
                                                            <Select
                                                                placeholder="Select comparison type"
                                                                options={comparisonOptions}
                                                                data-testid={`pipeline-pivot-sort-comparison-type-${name}`}
                                                            />
                                                        </Form.Item>
                                                    </Space>
                                                )}
                                            </>
                                        );
                                    }}
                                </Form.Item>

                                <Form.Item
                                    {...restField}
                                    label="Output Column"
                                    name={[name, 'outputColumn']}
                                    rules={[{ required: true, message: 'Enter an output column name' }]}
                                >
                                    <Input
                                        placeholder="e.g., total_amount"
                                        data-testid={`pipeline-pivot-output-column-${name}`}
                                    />
                                </Form.Item>
                            </div>
                        ))}

                        <Button
                            type="dashed"
                            icon={<PlusOutlined />}
                            onClick={() => add({ operation: 'sum', comparisonType: 'number', sortDirection: 'asc', sortComparisonType: 'date' })}
                            block
                            data-testid="pipeline-pivot-add-aggregation"
                        >
                            Add Aggregation
                        </Button>
                        <Form.ErrorList errors={errors} />
                    </div>
                )}
            </Form.List>
        </Form>
    );
};

export default PivotAggregateForm;
